import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useT } from "@/i18n/I18nContext";

function useCountUp(target, duration = 1600, start = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let rafId;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * target));
      if (p < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration, start]);
  return val;
}

function StatCounter({ value, suffix, label, delay }) {
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  const display = useCountUp(value, 1800, inView);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && setInView(true),
      { threshold: 0.3 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.9, delay }}
      className="text-center md:text-left"
    >
      <p className="font-serif text-[15vw] md:text-[7vw] lg:text-[6vw] leading-none tracking-tighter text-[var(--tt-text)]">
        {display}
        <span className="text-[var(--tt-gold)]">{suffix}</span>
      </p>
      <p className="mt-4 tt-overline text-[var(--tt-text-dim)] text-[11px] max-w-[240px] md:max-w-none mx-auto md:mx-0 leading-relaxed">
        {label}
      </p>
    </motion.div>
  );
}

export default function SocialProof() {
  const { t } = useT();
  const stats = t("proof.stats");
  return (
    <section
      id="social-proof"
      data-testid="social-proof-section"
      className="tt-section relative border-y border-[var(--tt-border)]"
      style={{ background: "var(--tt-surface)" }}
    >
      <div className="mx-auto max-w-[1480px] px-6 md:px-12">
        <div className="max-w-2xl mb-16">
          <p className="tt-overline">{t("proof.overline")}</p>
          <h2 className="mt-4 font-serif text-4xl md:text-6xl leading-[1.02] text-[var(--tt-text)]">
            {t("proof.heading_1")}{" "}
            <em className="italic text-[var(--tt-gold)]">{t("proof.heading_2")}</em>.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
          {stats.map((s, i) => (
            <StatCounter
              key={i}
              value={s.value}
              suffix={s.suffix}
              label={s.label}
              delay={i * 0.15}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
